/**
 * Hala CRM - All Requests Page
 * Shows all requests based on user role and scope
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Search, Filter, RefreshCw, Clock, CheckCircle, AlertCircle,
  User, ChevronRight, Inbox, UserCheck, XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const STATUS_CONFIG = {
  NEW: { label: 'جديد', color: COLORS.info, bg: COLORS.infoLight, icon: FileText },
  IN_POOL: { label: 'في المجمع', color: COLORS.warning, bg: COLORS.warningLight, icon: Inbox },
  ASSIGNED: { label: 'مُسند', color: COLORS.primary, bg: `${COLORS.primary}20`, icon: UserCheck },
  IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.gold, bg: `${COLORS.gold}20`, icon: Clock },
  COMPLETED: { label: 'مكتمل', color: COLORS.success, bg: COLORS.successLight, icon: CheckCircle },
  CANCELLED: { label: 'ملغي', color: COLORS.error, bg: COLORS.errorLight, icon: XCircle },
  UNASSIGNED: { label: 'غير مُسند', color: COLORS.textMuted, bg: COLORS.bgDark, icon: AlertCircle },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.UNASSIGNED;
  const Icon = config.icon;
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      borderRadius: '20px',
      backgroundColor: config.bg,
      color: config.color,
      fontSize: '12px',
      fontWeight: 500,
    }}>
      <Icon size={14} />
      {config.label}
    </span>
  );
}

export default function CRMRequestsPage() {
  const { user, getCRMHeaders } = useOutletContext();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    program: '',
    search: '',
  });

  const fetchRequests = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.program) params.append('program', filters.program);
      
      const [reqRes, statsRes] = await Promise.all([
        axios.get(`${API}/requests/for-user/${user.user_id}?${params}`, { headers: getCRMHeaders() }),
        axios.get(`${API}/requests/stats`, { headers: getCRMHeaders() }),
      ]);
      
      setRequests(reqRes.data.items || []);
      setStats(statsRes.data);
    } catch (e) {
      console.error('Failed to fetch requests:', e);
    } finally {
      setLoading(false);
    }
  }, [user, filters, getCRMHeaders]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
          جميع الطلبات
        </h1>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
          عرض وإدارة الطلبات حسب صلاحياتك ({requests.length} طلب)
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {Object.entries(STATUS_CONFIG).slice(0, 5).map(([key, config]) => {
            const count = stats.by_status?.[key] || 0;
            const Icon = config.icon;
            return (
              <div key={key} style={{
                backgroundColor: COLORS.white,
                padding: '16px',
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }} onClick={() => setFilters({ ...filters, status: filters.status === key ? '' : key })}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: config.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Icon size={20} color={config.color} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>{config.label}</p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: config.color }}>{count}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        alignItems: 'center',
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="بحث..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 14px 10px 40px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              textAlign: 'right',
            }}
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          style={{
            padding: '10px 14px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: COLORS.white,
            minWidth: '140px',
          }}
        >
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
        <button
          onClick={fetchRequests}
          style={{
            padding: '10px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            backgroundColor: COLORS.white,
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={18} color={COLORS.textMuted} />
        </button>
      </div>

      {/* Requests Table */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            جاري التحميل...
          </div>
        ) : requests.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            <FileText size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
            <p>لا توجد طلبات</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.bg }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>رقم الطلب</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>مقدم الطلب</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>الخدمة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>البرنامج</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>التاريخ</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr 
                  key={req.request_id} 
                  style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer' }}
                  onClick={() => navigate(`/crm/request/${req.request_id}`)}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.primary }}>
                      {req.request_id?.substring(0, 16)}...
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: COLORS.bgDark,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <User size={16} color={COLORS.textMuted} />
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                          {req.applicant_name || 'غير محدد'}
                        </p>
                        <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>
                          {req.mobile}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '13px', color: COLORS.text }}>
                      {req.service_name_ar || 'غير محدد'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      backgroundColor: COLORS.bgDark,
                      fontSize: '12px',
                      color: COLORS.text,
                    }}>
                      {req.program_name_ar || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <StatusBadge status={req.status} />
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '12px', color: COLORS.textMuted }}>
                      {formatDate(req.created_at)}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'left' }}>
                    <ChevronRight size={18} color={COLORS.textMuted} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
